import { useState } from "react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Loader2 } from "lucide-react"
import { addQuestions, updateQuestion } from "@/lib/api"
import type { QuestionnaireItem, UpdateQuestionInput } from "@/types/questionnaire-types"
import { toast } from "sonner"

// ===================
// Types
// ===================

interface AddQuestionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  artifactId: string
  onSuccess: () => void
  /** Pre-populated question for editing mode */
  editQuestion?: QuestionnaireItem | null
}

// ===================
// Component
// ===================

export function AddQuestionDialog({
  open,
  onOpenChange,
  artifactId,
  onSuccess,
  editQuestion,
}: AddQuestionDialogProps) {
  const isEditing = !!editQuestion

  const [number, setNumber] = useState("")
  const [prompt, setPrompt] = useState("")
  const [wordLimit, setWordLimit] = useState("")
  const [groupTitle, setGroupTitle] = useState("")
  const [weight, setWeight] = useState("")
  const [guidance, setGuidance] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Populate form when editing
  const populateForm = () => {
    if (editQuestion) {
      setNumber(editQuestion.number)
      setPrompt(editQuestion.prompt)
      setWordLimit(editQuestion.wordLimit?.toString() ?? "")
      setGroupTitle(editQuestion.groupTitle ?? "")
      setWeight(editQuestion.weight?.toString() ?? "")
      setGuidance(editQuestion.guidance ?? "")
    } else {
      resetForm()
    }
  }

  const resetForm = () => {
    setNumber("")
    setPrompt("")
    setWordLimit("")
    setGroupTitle("")
    setWeight("")
    setGuidance("")
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      setIsSubmitting(true)

      if (isEditing && editQuestion) {
        const updates: UpdateQuestionInput = {
          number: number.trim(),
          prompt: prompt.trim(),
          wordLimit: wordLimit ? parseInt(wordLimit) : null,
          weight: weight ? parseInt(weight) : null,
          groupTitle: groupTitle.trim() || null,
          guidance: guidance.trim() || null,
        }
        await updateQuestion(artifactId, editQuestion.sectionKey, updates)
        toast.success("Question updated")
      } else {
        await addQuestions(artifactId, [{
          number: number.trim(),
          prompt: prompt.trim(),
          responseType: 'rich_text',
          mandatory: true,
          wordLimit: wordLimit ? parseInt(wordLimit) : undefined,
          weight: weight ? parseInt(weight) : undefined,
          groupTitle: groupTitle.trim() || undefined,
          guidance: guidance.trim() || undefined,
        }])
        toast.success("Question added")
      }

      onSuccess()
      onOpenChange(false)
      resetForm()
    } catch {
      toast.error(isEditing ? "Failed to update question" : "Failed to add question")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value) resetForm()
        onOpenChange(value)
      }}
    >
      <DialogContent className="sm:max-w-lg" onOpenAutoFocus={populateForm}>
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Question" : "Add Question"}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Update the question details."
              : "Add a question from the tender document."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => void handleSubmit(e)}>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto px-1 -mx-1">
            {/* Number + Group Title row */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="q-number">Question number</Label>
                <Input
                  id="q-number"
                  placeholder="1.1"
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="q-group">Group (optional)</Label>
                <Input
                  id="q-group"
                  placeholder="Technical Capability"
                  value={groupTitle}
                  onChange={(e) => setGroupTitle(e.target.value)}
                />
              </div>
            </div>

            {/* Question text */}
            <div className="space-y-2">
              <Label htmlFor="q-prompt">Question text</Label>
              <Textarea
                id="q-prompt"
                placeholder="Describe your approach to..."
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={4}
                required
              />
            </div>

            {/* Word limit + Weight row */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="q-word-limit">Word limit (optional)</Label>
                <Input
                  id="q-word-limit"
                  type="number"
                  min={1}
                  placeholder="500"
                  value={wordLimit}
                  onChange={(e) => setWordLimit(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="q-weight">Weight % (optional)</Label>
                <Input
                  id="q-weight"
                  type="number"
                  min={0}
                  max={100}
                  placeholder="20"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                />
              </div>
            </div>

            {/* Guidance */}
            <div className="space-y-2">
              <Label htmlFor="q-guidance">Scoring guidance (optional)</Label>
              <Textarea
                id="q-guidance"
                placeholder="What evaluators look for..."
                value={guidance}
                onChange={(e) => setGuidance(e.target.value)}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !number.trim() || !prompt.trim()}
            >
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {isEditing ? "Save Changes" : "Add Question"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
